import { ClockIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { svgLoading } from 'stories/assets'
import { Color } from 'stories/types'
import { fillObject, isPromise } from 'utils'
import { getTextWidth } from 'utils/textWidth'

import { Tooltip } from '../Tooltip/Tooltip'

interface ISearchableSelectProps {
  id?: string
  title: string
  name?: string
  value: string
  error?: string
  options: string[] | Record<string, string> | Function
  renderItem?: Function
  required?: boolean
  className?: string
  tooltip?: string
  sort?: boolean
  /**
   * What background color to use
   */
  bgColor?: string
  color?: Color
  /**
   * Is Full
   */
  full?: boolean
  /**
   * Is disabled
   */
  disabled?: boolean
  /**
   * Is readOnly
   */
  readOnly?: boolean

  additionalElements?: JSX.Element | null
  copyable?: boolean
  hasDefaultOption?: boolean
  defaultOptionText?: string
  history?: boolean
  prefix?: string

  onChange?: (value: any) => void
  showHistory?: () => void
}

export function SearchableSelect({
  title,
  value,
  error = '',
  options,
  renderItem,
  required,
  className,
  tooltip = '',
  bgColor = '',
  color = 'blue',
  disabled = false,
  readOnly = false,
  additionalElements,
  copyable = false,
  sort = false,
  hasDefaultOption = false,
  defaultOptionText = '- Select -',
  history = false,
  onChange = () => {},
  showHistory = () => {},
  prefix = '',
}: ISearchableSelectProps) {
  const width = getTextWidth(title)
  const classNames = [
    'block',
    'rounded-xl',
    'px-2.5',
    'pb-[2px]',
    width < window.innerWidth - 20 ? 'pt-[27px]' : 'pt-[45px]',
    'w-full',
    'text-[15px]',
    'text-gray-900',
    'disabled:opacity-100',
    bgColor ? `bg-${bgColor}` : disabled ? 'bg-slate-100' : 'bg-white',
    readOnly ? 'cursor-not-allowed' : '',
    'border',
    'border-slate-300',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-shade-blue/20',
    'focus:bg-white',
    'transition',
    'hover:border-slate-400',
    'focus:border-shade-blue',
    'peer',
    prefix.length > 0 && 'pl-7',
    error && 'border-rose-700',
    disabled && 'cursor-not-allowed',
  ]

  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [tempOptions, setTempOptions] = useState<Record<string, string>>({})
  const [filteredOptions, setFilteredOptions] = useState<Record<string, string>>({})
  const [isLoading, setLoading] = useState(false)
  const [isGetOnce, setIsGetOnce] = useState(false)

  const isFiltered = (option: string, query: string) => option.toLowerCase().includes(query.toLowerCase())

  const onUpdateOptions = async (searchTerm: string) => {
    setIsGetOnce(true)

    let newOptions: Record<string, string> = {}
    if (typeof options == 'object') {
      let optionRec: Record<string, string> = {}

      if (!Array.isArray(options)) optionRec = options
      else optionRec = fillObject(options, (v: string) => v)

      Object.keys(optionRec).forEach((key) => {
        if (isFiltered(optionRec[key], searchTerm)) newOptions[key] = optionRec[key]
      })
    } else if (typeof options == 'function') {
      const _newOptions = options(searchTerm)
      const isPro = isPromise(_newOptions)
      if (isPro) setLoading(true)
      newOptions = isPro ? await _newOptions : _newOptions
      setLoading(false)
    }
    if (isOpen) setTempOptions(newOptions)
    else setFilteredOptions(newOptions)
  }

  useEffect(() => {
    if (typeof options != 'function') {
      onUpdateOptions(searchTerm)
      return
    }

    if (value && !isNaN(Number(value)) && typeof options === 'function' && !isGetOnce) {
      onUpdateOptions(value == '0' ? '' : value)
      return
    }

    if (!isOpen) return

    const timeOutId = setTimeout(() => !isLoading && onUpdateOptions(searchTerm), 700)
    return () => clearTimeout(timeOutId)
  }, [options, searchTerm])

  useEffect(() => {
    if (isOpen) setTempOptions(filteredOptions)
  }, [isOpen])

  const copyValue = () => {
    toast('Copied to your Clipboard!', { type: 'info' })
    navigator.clipboard.writeText(value)
  }

  const onCloseOptions = () => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const renderValue = () => {
    if (!value) {
      if (hasDefaultOption) return defaultOptionText
      else return '--'
    }
    return renderItem ? renderItem(filteredOptions[value]) : filteredOptions[value]
  }

  const renderOptions = () => {
    const currentOptions = isOpen ? tempOptions : filteredOptions
    if (Object.keys(currentOptions).length == 0) return <li className="px-4 py-2 text-gray-500">No results found</li>

    let _options: any = []
    Object.keys(currentOptions).map((key) => {
      if (currentOptions[key] === null) return
      _options.push({
        key: key,
        value: currentOptions[key],
      })
    })

    if (sort) {
      _options = _options.sort(function (a: any, b: any) {
        return a.value.toLowerCase().localeCompare(b.value.toLowerCase())
      })
    }
    let rlt: any = []
    _options.map((item: any) => {
      const isActive = item.key == value

      rlt.push(
        <li
          key={item.key}
          onClick={() => {
            onChange(item.key)
            setFilteredOptions(tempOptions)
            onCloseOptions()
          }}
          className={`px-3 py-1 hover:bg-${color}-500 hover:text-white cursor-pointer text-sm ${
            isActive ? `bg-${color}-700 text-white` : ''
          }`}
        >
          {renderItem ? renderItem(item.value) : item.value}
        </li>,
      )
    })

    return rlt
  }

  return (
    <div
      className={`input-container relative ${isOpen ? 'z-50' : ''}`}
      onBlur={() => setTimeout(() => isOpen && onCloseOptions(), 300)}
    >
      <div className={`group relative z-0 w-full group ${className}`}>
        <label className="absolute text-[12px] text-gray-700 top-1.5 z-10 origin-[0] left-2.5 flex gap-2 items-center">
          {title}
          {required && '*'}
          {tooltip.length > 0 ? <Tooltip message={tooltip}></Tooltip> : null}
          {additionalElements !== null && additionalElements}
          {history && (
            <span className="ml-1 hidden group-hover:inline" onClick={() => showHistory()}>
              <ClockIcon className="h-[14px] w-[14px] text-gray-500 cursor-pointer" aria-hidden="true" />
            </span>
          )}
          {copyable && (
            <span className="ml-1 hidden group-hover:inline" onClick={() => copyValue()}>
              <DocumentDuplicateIcon className="h-[14px] w-[14px] text-gray-600 cursor-pointer" aria-hidden="true" />
            </span>
          )}
        </label>

        <div
          className={`${classNames.join(' ')} p-2 cursor-pointer bg-white min-h-[53.5px] truncate`}
          onClick={() => {
            isOpen && setSearchTerm('')
            setIsOpen(!isOpen)
          }}
        >
          {renderValue()}
        </div>
      </div>
      {error && <p className="peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1">{error}</p>}

      {isOpen && (
        <div className={`absolute z-20 mt-1 w-full bg-white border border-${color}-600 shadow-lg`}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 outline-none"
            autoFocus
          />
          <ul className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <li>
                <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
              </li>
            ) : (
              renderOptions()
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
